
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useOrchestratorAgent } from "@/hooks/useOrchestratorAgent";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner"; // Keep toast here as mutations use it directly via configs

import { EmailSummaryForHook } from "./types";
import { fetchProcessedEmailsFn } from "./emailDataService";
import { 
  getProcessEmailsMutationConfig,
  getArchiveEmailMutationConfig,
  getFlagEmailMutationConfig
} from "./emailMutationService";

export const useEmailProcessor = (emailsToProcessPerRequest: number = 5) => {
  const queryClient = useQueryClient();
  const { invokeAgent } = useOrchestratorAgent();
  
  const { data: emailSummaries = [], isLoading: isFetchingEmails } = useQuery<EmailSummaryForHook[], Error>({
    queryKey: ['processed-emails'],
    queryFn: () => fetchProcessedEmailsFn(supabase)
  });

  const processEmailsMutation = useMutation(
    getProcessEmailsMutationConfig(invokeAgent, queryClient, emailsToProcessPerRequest)
  );

  const archiveEmailMutation = useMutation(
    getArchiveEmailMutationConfig(invokeAgent, queryClient)
  );

  const flagEmailMutation = useMutation(
    getFlagEmailMutationConfig(invokeAgent, queryClient)
  );

  const handleProcessEmails = () => {
    processEmailsMutation.mutate();
  };

  const handleArchiveEmail = (emailId: string) => {
    archiveEmailMutation.mutate(emailId);
  };

  const handleFlagEmail = (emailId: string, currentFlagStatus: boolean) => {
    flagEmailMutation.mutate({ emailId, currentFlagStatus });
  };

  return {
    emailSummaries,
    isFetchingEmails,
    processEmailsMutation, // Expose the mutation itself for loading states etc.
    archiveEmailMutation,  // Expose the mutation itself
    flagEmailMutation,     // Expose the mutation itself
    handleProcessEmails,
    handleArchiveEmail,
    handleFlagEmail,
  };
};
